import React, { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { Lock, ArrowRight } from 'lucide-react'
import Seo from '../components/Seo'
import { STRIPE_PRICE_MAP } from '../lib/stripePriceMap'

/**
 * Generic enrollment + secure-checkout page for every package other than
 * The Strong Again Method™ (which keeps its own dedicated page). Reached by
 * a client after their consultation, at /enroll/:packageKey — not linked
 * anywhere on the public pricing page, same as the existing Strong Again
 * enrollment flow.
 */
const PackageEnrollment: React.FC = () => {
  const { packageKey = '' } = useParams<{ packageKey: string }>()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const pkg = STRIPE_PRICE_MAP[packageKey]

  const handleCheckout = async () => {
    setLoading(true)
    setError('')

    try {
      const response = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ packageKey }),
      })

      const data = await response.json()

      if (!response.ok || !data.url) {
        throw new Error(data.error || 'Unable to start checkout.')
      }

      window.location.href = data.url
    } catch (err) {
      console.error('Checkout error:', err)
      setError(
        'We were unable to start secure checkout. Please try again or contact Cassie for assistance.'
      )
      setLoading(false)
    }
  }

  if (!pkg || !pkg.priceId) {
    return (
      <main className="min-h-screen bg-brand-cream text-brand-navy flex items-center justify-center px-4">
        <div className="max-w-md text-center">
          <h1 className="font-display text-2xl font-semibold mb-4">
            This enrollment link isn't active yet
          </h1>
          <p className="text-brand-navy/65 mb-6">
            Please reach out to Cassie directly, or schedule a complimentary
            consultation to get started.
          </p>
          <Link
            to="/book-consultation"
            className="inline-flex items-center justify-center rounded-full bg-brand-navy text-brand-cream font-bold px-7 py-4 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-soft-lg"
          >
            Schedule Your Complimentary Consultation
            <ArrowRight className="ml-2" size={18} />
          </Link>
        </div>
      </main>
    )
  }

  const billingNote =
    pkg.mode === 'subscription'
      ? `By continuing to checkout, you are enrolling in ${pkg.packageName} at ${pkg.priceLabel}, billed on a recurring basis according to the terms presented during checkout and the Fit by Cassie Terms of Service.`
      : `By continuing to checkout, you are enrolling in ${pkg.packageName} for ${pkg.priceLabel}, paid in full, according to the terms presented during checkout and the Fit by Cassie Terms of Service.`

  return (
    <main className="min-h-screen bg-brand-cream text-brand-navy">
      <Seo
        title={`Enroll in ${pkg.packageName} — Fit by Cassie`}
        description={`Complete your enrollment in ${pkg.packageName} coaching with Cassie.`}
      />

      <section className="px-4 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-24">
        <div className="container-max">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-10">
              <span className="inline-block text-xs font-bold uppercase tracking-widest-xl text-brand-teal mb-4">
                Private Client Enrollment
              </span>

              <h1 className="font-display text-4xl sm:text-5xl font-semibold text-brand-navy leading-tight mb-5">
                {pkg.packageName}
              </h1>

              <p className="text-lg text-brand-navy/65 max-w-2xl mx-auto leading-relaxed">
                You're ready for the next step. Review your enrollment details
                below, then continue to secure checkout.
              </p>
            </div>

            <div className="bg-brand-ivory rounded-3xl border border-brand-bronze/25 shadow-soft-lg p-7 sm:p-10">
              <span className="inline-block rounded-full bg-brand-blush/20 px-4 py-2 text-xs font-bold uppercase tracking-widest text-brand-navy mb-6">
                Client Enrollment
              </span>

              <h2 className="font-display text-3xl font-semibold text-brand-navy mb-2">
                {pkg.packageName}
              </h2>

              <div className="rounded-2xl bg-brand-cream border border-brand-teal/15 p-6 sm:p-7 mb-8">
                <p className="text-xs font-bold uppercase tracking-widest text-brand-teal mb-2">
                  Investment
                </p>

                <p className="font-display text-4xl sm:text-5xl font-semibold text-brand-navy">
                  {pkg.priceLabel}
                </p>
              </div>

              <div className="border-t border-brand-bronze/20 pt-7 mb-7">
                <h3 className="font-display text-xl font-semibold text-brand-navy mb-3">
                  Your commitment
                </h3>

                <p className="text-sm text-brand-navy/65 leading-relaxed">
                  {billingNote}
                </p>
              </div>

              {error && (
                <div className="rounded-2xl bg-red-50 border border-red-200 p-4 mb-6">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              <button
                type="button"
                onClick={handleCheckout}
                disabled={loading}
                className="inline-flex w-full items-center justify-center rounded-full bg-brand-navy text-brand-cream font-bold px-7 py-4 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-soft-lg disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0"
              >
                {loading ? (
                  'Opening Secure Checkout...'
                ) : (
                  <>
                    Enroll & Continue to Secure Checkout
                    <ArrowRight className="ml-2" size={18} />
                  </>
                )}
              </button>

              <div className="flex items-center justify-center gap-2 mt-4 text-xs text-brand-navy/50">
                <Lock size={13} />
                Secure checkout powered by Stripe
              </div>

              <p className="text-center text-xs text-brand-navy/45 mt-3">
                Your payment information is entered securely during checkout.
              </p>
            </div>
          </div>
        </div>
      </section>
    </main>
  )
}

export default PackageEnrollment
